import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import SiteDialog from "../../components/SiteDialog";
import AppPagination from "../../components/AppPagination";
import theme from "../../utils/theme";
import { useAppSelector } from "../../hooks";
import { BasicDataTypes } from "../../types/types";
import { useLocation } from "react-router-dom";
import Loading from "../../components/Loading";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useVisaTypeRequest } from "../../hooks/visaTypeRequest";
import CreateVisa from "../../components/visa/CreateVisa";
import UpdateVisa from "../../components/visa/UpdateVisa";
import AlertDialog from '../../components/AlertDialog';
import SearchInput from '../../components/filters/SearchInput';
import RefreshButton from '../../components/filters/RefreshButton';

const VisaTypesList: React.FC = () => {
  const { search } = useLocation();
  const { loading, data: visaTypesData } = useAppSelector(state => state.visa);
  const [visaTypes, setVisaTypes] = useState<BasicDataTypes[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [lastPage, setLastPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(0);
  const [createOpen, setCreateOpen] = useState<boolean>(false);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [updateOpen, setUpdateOpen] = useState<boolean>(false);
  const [visa, setVisa] = useState<BasicDataTypes | null>(null);
  const [slug, setSlug] = useState<string | null>(null);
  const [ searchText,setSearchText ] = useState<string>("")
  const visaRequest = useVisaTypeRequest();

  const handleEdit = useCallback((visa: BasicDataTypes) => {    
    setVisa(visa);
    setUpdateOpen(true);
  }, []);

  const fetchData = useCallback(async () => {
    try {
      await visaRequest({ method: 'GET', data: null, slug: null, query: null });
    } catch (error) {
      console.error('Failed to fetch visa:', error);
    }
  }, [visaRequest]);

  const handleDelete = (toDeleteSlug: string) => {
    setSlug(toDeleteSlug);
    setShowAlert(true);
  };

  const handleCancel = () => {
    setShowAlert(false);
  };

  const handleConfirm = useCallback(async () => {
    if (slug) {
      try {
        await visaRequest({ method: 'DELETE', data: null, slug, query: null });
        fetchData();
        setShowAlert(false);
      } catch (error) {
        console.error('Failed to delete visa:', error);
      }
    }
  }, [visaRequest, fetchData, slug]);

  useEffect(() => {
    if (visaTypesData) {
      const metaData = visaTypesData?.meta;
      setLastPage(Number(metaData?.lastPage) || 1);
      setCurrentPage(Number(metaData?.currentPage));
      setPerPage(Number(metaData?.perPage));
      setVisaTypes(visaTypesData?.data);
    }
  }, [visaTypesData]);

  useEffect(() => {
    fetchData();
  }, [search, fetchData]);

  return (
    <Grid container columns={{ xs: 4, sm: 8, md: 12 }}>
      <Grid item xs={12}>
        <Paper elevation={2} sx={{ p: 2, borderRadius: '8px' }}>
          <AlertDialog
            toggle={showAlert}
            setToggle={setShowAlert}
            cancel={handleCancel}
            confirm={handleConfirm}
            title={"Are you sure?"}
            content={"You want to delete this visa type."}
            type="error"
          />
          {loading && <Loading />}
          {visa && (
            <SiteDialog
              open={updateOpen}
              setOpen={setUpdateOpen}
              dialogChildren={<UpdateVisa visaData={visa} setDialogOpen={setUpdateOpen} />}
            />
          )}
          <Typography variant="h5" sx={{ mb: 3 }}>Visa Types List</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',mb: 5 }}>
            <Button variant="contained" onClick={() => setCreateOpen(true)}>Add New</Button>
            <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
              <SearchInput
                searchText={searchText}
                setSearchText={setSearchText}
                placeholderText='Search by title'
              />
              <Box sx={{ mr: 3 }}></Box>
              <RefreshButton/>
            </Box>
            <SiteDialog
              open={createOpen}
              setOpen={setCreateOpen}
              dialogChildren={<CreateVisa setDialogOpen={setCreateOpen} />}
            />
          </Box>
          <TableContainer sx={{ border: `1px solid ${theme.palette.grey[200]}`, borderRadius: '10px' }}>
            <Table aria-label="caption table">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">No</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="left">Title</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visaTypes.length > 0 ? visaTypes.map((visa, index) => (
                  <TableRow key={visa.id}>
                    <TableCell align="center">{((currentPage - 1) * perPage) + (index + 1)}</TableCell>
                    <TableCell align="left">{visa.name}</TableCell>
                    <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                      <Button onClick={() => handleEdit(visa)} sx={{ mr: 2 }} variant="contained" size="small" color="warning">
                        <EditIcon />
                      </Button>
                      <Button onClick={() => handleDelete(visa.slug)} variant="contained" size="small" color="error">
                        <DeleteIcon />
                      </Button>
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={3} align="center">No visa types found</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <AppPagination pageCount={Number(lastPage)} />
        </Paper>
      </Grid>
    </Grid>
  );
};

export default VisaTypesList;
